<template>
  <div class="reschedule-section">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1597634285596-01d570b98d0d?q=80&w=2070"
      title="Reschedule Visit"
      height="700px"
    />

    <!-- Wavy Transition -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <!-- Form Container -->
    <div class="container">
      <div v-if="submitted" class="success-message">
        <div class="success-icon">✓</div>
        <h2>Request Submitted Successfully!</h2>
        <p>
          We have received your rescheduling request. Our team will review and get back to you within 24 hours.
        </p>
        <button @click="resetForm" class="primary-btn">
          Submit Another Request
        </button>
      </div>

      <form v-else @submit.prevent="handleSubmit" class="reschedule-form">
        <h2>Reschedule Visit Request</h2>
        
        <div class="form-group">
          <label>Ticket Number</label>
          <input
            v-model="formData.ticketNumber"
            type="text"
            placeholder="Enter your ticket number"
            :class="{ 'error': errors.ticketNumber }"
          />
          <span v-if="errors.ticketNumber" class="error-message">{{ errors.ticketNumber }}</span>
        </div>

        <div class="date-group">
          <div class="form-group">
            <label>Current Visit Date</label>
            <input
              v-model="formData.currentDate"
              type="date"
              :class="{ 'error': errors.currentDate }"
            />
            <span v-if="errors.currentDate" class="error-message">{{ errors.currentDate }}</span>
          </div>

          <div class="form-group">
            <label>New Visit Date</label>
            <input
              v-model="formData.newDate"
              type="date"
              :min="minDate"
              :class="{ 'error': errors.newDate }"
            />
            <span v-if="errors.newDate" class="error-message">{{ errors.newDate }}</span>
          </div>
        </div>

        <div class="form-group">
          <label>Reason for Rescheduling</label>
          <textarea
            v-model="formData.reason"
            placeholder="Please provide the reason for rescheduling"
            :class="{ 'error': errors.reason }"
          ></textarea>
          <span v-if="errors.reason" class="error-message">{{ errors.reason }}</span>
        </div>

        <div class="contact-group">
          <div class="form-group">
            <label>Full Name</label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Enter your full name"
              :class="{ 'error': errors.name }"
            />
            <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
          </div>

          <div class="form-group">
            <label>Email Address</label>
            <input
              v-model="formData.email"
              type="email"
              placeholder="Enter your email"
              :class="{ 'error': errors.email }"
            />
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <div class="form-group">
            <label>Phone Number</label>
            <input
              v-model="formData.phone"
              type="tel"
              placeholder="Enter your phone number"
              :class="{ 'error': errors.phone }"
            />
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
          </div>
        </div>

        <div class="policy-notes">
          <h3>Please Note:</h3>
          <ul>
            <li>Rescheduling requests must be submitted at least 24 hours before the visit date</li>
            <li>Tickets can only be rescheduled once</li>
            <li>New visit date must be within 30 days of the original date</li>
            <li>Approval is subject to availability</li>
          </ul>
        </div>

        <button type="submit" class="submit-btn">Submit Request</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Banner from "@/components/Banner.vue";

const formData = ref({
  ticketNumber: '',
  currentDate: '',
  newDate: '',
  reason: '',
  name: '',
  email: '',
  phone: ''
});

const errors = ref({});
const submitted = ref(false);

const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

const validateForm = () => {
  const newErrors = {};
  
  if (!formData.value.ticketNumber) newErrors.ticketNumber = 'Ticket number is required';
  if (!formData.value.currentDate) newErrors.currentDate = 'Current visit date is required';
  if (!formData.value.newDate) newErrors.newDate = 'New visit date is required';
  if (!formData.value.reason) newErrors.reason = 'Reason is required';
  if (!formData.value.name) newErrors.name = 'Name is required';
  if (!formData.value.email) newErrors.email = 'Email is required';
  if (!formData.value.phone) newErrors.phone = 'Phone number is required';

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

const handleSubmit = () => {
  if (validateForm()) {
    // Here you would typically make an API call to submit the form
    console.log('Form submitted:', formData.value);
    submitted.value = true;
  }
};

const resetForm = () => {
  formData.value = {
    ticketNumber: '',
    currentDate: '',
    newDate: '',
    reason: '',
    name: '',
    email: '',
    phone: ''
  };
  errors.value = {};
  submitted.value = false;
};
</script>

<style scoped>
.reschedule-section {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
}

.reschedule-form {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

h2 {
  color: #1268B1;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2rem;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

input, textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
}

textarea {
  height: 120px;
  resize: vertical;
}

.date-group, .contact-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.error {
  border-color: #dc3545;
}

.error-message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.policy-notes {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin: 30px 0;
}

.policy-notes h3 {
  color: #1268B1;
  margin-bottom: 10px;
}

.policy-notes ul {
  padding-left: 20px;
}

.policy-notes li {
  margin-bottom: 8px;
  color: #666;
}

.submit-btn {
  width: 100%;
  padding: 15px;
  background: #1268B1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-btn:hover {
  background: #0d4f8c;
}

.success-message {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.success-icon {
  font-size: 48px;
  color: #28a745;
  margin-bottom: 20px;
}

.success-message h2 {
  color: #1268B1;
  margin-bottom: 20px;
}

.success-message p {
  color: #666;
  margin-bottom: 30px;
}

.primary-btn {
  background: #1268B1;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
}

.primary-btn:hover {
  background: #0d4f8c;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .reschedule-form {
    padding: 20px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}
</style>